import { Diagnostic } from "../models/diagnostic.js";

/**
 * Checks for Android SDK related issues.
 * RULES:
 * 1. ANDROID SDK MISSING (ANDROID_HOME and ANDROID_SDK_ROOT missing)
 * 2. ADB NOT FOUND (adb unavailable)
 */
export function checkAndroidDiagnostic(
  androidHome: string | undefined,
  sdkRoot: string | undefined,
  adbAvailable: boolean
): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];

  // 1. ANDROID SDK MISSING
  if (!androidHome && !sdkRoot) {
    diagnostics.push({
      id: "android-sdk-missing",
      title: "Android SDK Missing",
      severity: "error",
      detected: "Neither ANDROID_HOME nor ANDROID_SDK_ROOT environment variables are set.",
      explanation: "React Native Android builds require the Android SDK to compile, build, and deploy your application.",
      symptoms: [
        "Unable to build or run the project on Android emulators or devices",
        "Android build failures inside Gradle (e.g. sdk directory not found)",
      ],
      fixes: [
        "Download and install Android Studio (https://developer.android.com/studio).",
        "Setup the Android SDK, Tools, and Emulator in Android Studio.",
        "Set the ANDROID_HOME environment variable pointing to your Android SDK path (e.g. $HOME/Library/Android/sdk on macOS).",
      ],
      documentationUrl: "https://reactnative.dev/docs/environment-setup",
    });
  }

  // 2. ADB NOT FOUND
  if (!adbAvailable) {
    diagnostics.push({
      id: "adb-not-found",
      title: "ADB Tool Unavailable",
      severity: "warning",
      detected: "adb command not found in your PATH environment variable.",
      explanation: "Android Debug Bridge (adb) is required to deploy, install, run, and debug your application on Android devices and emulators.",
      symptoms: [
        "Cannot launch React Native applications on running emulators or connected devices",
        "Cannot reverse ports for debugging (adb reverse tcp:8081 tcp:8081)",
      ],
      fixes: [
        "Ensure Android SDK Platform-Tools are installed via Android Studio's SDK Manager.",
        "Add the platform-tools directory of your Android SDK to your PATH variable.",
        "On macOS/Linux, add: export PATH=$PATH:$ANDROID_HOME/platform-tools to your shell config.",
      ],
      documentationUrl: "https://developer.android.com/studio/intro/studio-config",
    });
  }

  return diagnostics;
}
